import { useId } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { useField } from 'formik'
import { snakeCase } from 'lodash'
import { changeHeadingObjectStyleType, changeHeadingOrientation, getHeadingCombinedStyleName } from '../ArticleParse'
import { DirtyEffect } from '../CommonComponents'
import type { Article, Heading, LayoutableSetting } from '../types'
import { Orientation, type OrientationValue } from '../../common/types'
import { fieldNameWithParent } from '../articleHeadings'

interface EnumValues {
  headingOrientations: Record<string, string>
  horizontalHeadingObjectStyleTypes: Record<string, string>
}

interface Props {
  name: string
  enumValues: EnumValues
  layoutable: { setting: LayoutableSetting }
  formEditable: boolean
  basicNormalHeadings: Array<{ base: { type: string } }>
  showExperimentalFeatures: boolean
}

export const HeadingOrientationField = ({
  name,
  enumValues,
  layoutable,
  formEditable,
  basicNormalHeadings,
  showExperimentalFeatures
}: Props) => {
  // 通常見出しの縦横の向きを設定するフィールド
  const checkboxId = useId()

  const [orientationField, orientationMeta, orientationHelpers] = useField(
    fieldNameWithParent('data.headingOrientation', name)
  )
  const [styleTypeField, styleTypeMeta, styleTypeHelpers] = useField(
    fieldNameWithParent('data.headingObjectStyleType', name)
  )
  const [columnsField, columnsMeta, columnsHelpers] = useField(
    fieldNameWithParent('data.numberOfOccupyingColumns', name)
  )
  const [rowsField, rowsMeta, rowsHelpers] = useField(fieldNameWithParent('data.numberOfOccupyingRows', name))
  const [_combinationTypeField, combinationTypeMeta, combinationTypeHelpers] = useField(
    fieldNameWithParent('data.headingCombinationSettingType', name)
  )

  const orientation = orientationField.value
  const isHorizontal = orientation === Orientation.Horizontal
  const isDirty = orientation !== orientationMeta.initialValue

  const onChange: React.ChangeEventHandler<HTMLElement> = (e) => {
    const newOrientation = (e.target as HTMLInputElement).value as OrientationValue

    orientationHelpers.setValue(newOrientation)

    if (newOrientation !== orientationMeta.initialValue) {
      const inputArticle = {
        headingNumberOfOccupyingRows: rowsField.value,
        headingNumberOfOccupyingColumns: columnsField.value
      } as Article
      const headingTypes = basicNormalHeadings.map((h) => ({ headingType: h.base.type })) as Heading[]
      const {
        headingNumberOfOccupyingColumns,
        headingNumberOfOccupyingRows,
        headingObjectStyleType,
        headingCombinationSettingType
      } = changeHeadingOrientation({
        article: inputArticle,
        basicNormalHeadings: headingTypes,
        newOrientation,
        setting: layoutable.setting
      })
      console.log('changeHeadingOrientation', { headingCombinationSettingType })
      styleTypeHelpers.setValue(headingObjectStyleType)
      columnsHelpers.setValue(headingNumberOfOccupyingColumns)
      rowsHelpers.setValue(headingNumberOfOccupyingRows)
      if (showExperimentalFeatures) {
        combinationTypeHelpers.setValue(headingCombinationSettingType)
      }
    } else {
      styleTypeHelpers.setValue(styleTypeMeta.initialValue)
      columnsHelpers.setValue(columnsMeta.initialValue)
      rowsHelpers.setValue(rowsMeta.initialValue)
      if (showExperimentalFeatures) {
        combinationTypeHelpers.setValue(combinationTypeMeta.initialValue)
      }
    }
  }

  const orientationRadioButtons = Object.keys(enumValues.headingOrientations).map((k) => (
    <Form.Check
      key={`heading-${checkboxId}-${k}`}
      id={`heading-${checkboxId}-${k}`}
      inline
      label={`${enumValues.headingOrientations[k]}見出し`}
      type='radio'
      name='orientation'
      checked={k === orientation}
      value={k}
      onChange={onChange}
      disabled={!formEditable}
    />
  ))

  const headingObjectStyleType = styleTypeField.value
  const initialHeadingObjectStyleType = styleTypeMeta.initialValue
  const isStyleTypeDirty = isHorizontal && headingObjectStyleType !== initialHeadingObjectStyleType

  const onStyleTypeChange: React.ChangeEventHandler<HTMLElement> = (e) => {
    const newStyleType = (e.target as HTMLSelectElement).value
    styleTypeHelpers.setValue(newStyleType)

    if (!isHorizontal) {
      columnsHelpers.setValue(columnsMeta.initialValue)
      rowsHelpers.setValue(rowsMeta.initialValue)
    } else {
      const headingCombinationSettingType = getHeadingCombinedStyleName(newStyleType)
      const headingNumberOfOccupyingRows = changeHeadingObjectStyleType({
        headingObjectStyleType: newStyleType,
        setting: layoutable.setting
      })
      const headingNumberOfOccupyingColumns = 1
      columnsHelpers.setValue(headingNumberOfOccupyingColumns)
      rowsHelpers.setValue(headingNumberOfOccupyingRows)
      if (showExperimentalFeatures) {
        combinationTypeHelpers.setValue(headingCombinationSettingType)
      }
    }
  }

  const onStyleTypeBlur = (_e: React.FocusEvent) => {}

  const styleSelectControl =
    isHorizontal && !showExperimentalFeatures ? (
      <DirtyEffect dirty={isStyleTypeDirty}>
        <Form.Control
          as='select'
          type='text'
          size='sm'
          name='headingObjectStyleType'
          defaultValue={headingObjectStyleType}
          onChange={onStyleTypeChange}
          onBlur={onStyleTypeBlur}
          isInvalid={undefined}
        >
          {Object.keys(enumValues.horizontalHeadingObjectStyleTypes).map((k) => (
            <option key={`headingObjectStyleType-option-${k}`} value={snakeCase(k)}>
              {enumValues.horizontalHeadingObjectStyleTypes[k]}
            </option>
          ))}
        </Form.Control>
        <Form.Control.Feedback type='invalid'>{undefined}</Form.Control.Feedback>
      </DirtyEffect>
    ) : undefined

  return (
    <DirtyEffect dirty={isDirty}>
      <Row>
        <Col sm={5}>{orientationRadioButtons}</Col>
        <Col>{styleSelectControl}</Col>
      </Row>
    </DirtyEffect>
  )
}
